import { Filter } from 'app/common/table/Filter';
import { CfmuFlightType, FlightDto } from 'app/model/CORE';
import KeyUtils from 'app/common/util/KeyUtils';
import Utils from 'app/common/util/Utils';
import TimeUtils from 'app/common/util/TimeUtils';

export interface FlightListFilter {
    name: string;
    filter: () => Filter<FlightDto>;
}

export class FlightTextFilter implements FlightListFilter {
    name = 'TEXT_FILTER';
    private readonly text: string = '';
    constructor(text: string) {
        this.text = text;
    }
    filter = (): Filter<FlightDto> => {
        const words = this.text && Utils.toUpperCase(this.text).split(' ');
        return {
            keep(elem: FlightDto): boolean {
                if (words) {
                    const callsign = Utils.toUpperCase(elem.flightId?.flightKeys?.callsign);
                    const adep = Utils.toUpperCase(elem.flightId?.flightKeys?.adep);
                    const ades = Utils.toUpperCase(elem.flightId?.flightKeys?.ades);

                    // Check that every word provided match with at least one field of the flight
                    return words.every(word => {
                        if (word.startsWith('>', 0)) {
                            const re = Utils.wildcardToRegex(word.slice(1));
                            return re.test(ades);
                        } else if (word.startsWith('<', 0)) {
                            const re = Utils.wildcardToRegex(word.slice(1));
                            return re.test(adep);
                        } else {
                            const re = Utils.wildcardToRegex(word);
                            return re.test(callsign) || re.test(adep) || re.test(ades);
                        }
                    });
                }
                return true;
            },
        };
    };
}

export class SelectedFlightsOnlyFilter implements FlightListFilter {
    name = 'SELECTED_ONLY_FILTER';
    private readonly selection: Array<FlightDto>;
    constructor(selection: Array<FlightDto>) {
        this.selection = selection;
    }
    filter = (): Filter<FlightDto> => {
        const keyProvider = KeyUtils.FLIGHT_KEY_PROVIDER;
        const selectedFlights = this.selection;
        return {
            keep(elem: FlightDto): boolean {
                const key = KeyUtils.getKey(keyProvider, elem);
                const value = KeyUtils.getValueFromKey(selectedFlights, key, keyProvider);
                return Utils.isExists(value);
            },
        };
    };
}

export class DelayedFlightsOnlyFilter implements FlightListFilter {
    name = 'DELAYED_ONLY_FILTER';
    private readonly threshold: number;
    constructor(threshold: number) {
        this.threshold = threshold;
    }

    filter = (): Filter<FlightDto> => {
        const thresholdInMs = this.threshold * TimeUtils.MIN_TO_MS;
        return {
            keep(elem: FlightDto): boolean {
                return elem.delayInMs >= thresholdInMs;
            },
        };
    };
}

export class CfmuTypesFilter implements FlightListFilter {
    name = 'CFMU_TYPES_FILTER';
    private readonly types: CfmuFlightType[];
    constructor(types: CfmuFlightType[]) {
        this.types = types;
    }

    filter = (): Filter<FlightDto> => {
        const cfmuTypes = this.types;
        return {
            keep(elem: FlightDto): boolean {
                return cfmuTypes.includes(elem.cfmuFlightType);
            },
        };
    };
}
